import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Flex,
  Modal,
  Input,
  Select,
  Form,
  Row,
  Col,
  Typography,
  notification,
  Button,
  Upload,
  UploadFile,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import styles from "pages/master/finance/finance.module.scss";
import { UploadChangeParam } from "antd/es/upload/interface";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { useParams, useNavigate } from "react-router-dom";
import {
  MasterInvoice,
  MasterAccrual,
  MasterListItem,
} from "pages/master/finance/types";
import useMasterInvoiceStore from "stores/master/masterInvoice";
import { ObjectType } from "pages/admin/objectsAndClients/objects/types";

const { Text } = Typography;
const { TextArea } = Input;

type ComponentProps = {};

const { Title } = Typography;

const Page: React.FC<ComponentProps> = () => {
  const { "*": subRoute } = useParams();
  const navigate = useNavigate();

  const isAddMode = useMemo(() => {
    if (subRoute === "add") return true;

    return false;
  }, [subRoute]);

  const {
    list,
    setList,
    objectsList,
    setObjectsList,
    isPostInProcess,
    setIsPostInProcess,
    isSubmittable,
    setIsSubmittable,
    isError,
    setIsError,
    isFileUploading,
    setIsFileUploading,
    isChangeStatusInProcess,
    setIsChangeStatusInProcess,
  } = useMasterInvoiceStore((store) => store);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const listRef = useRef(list);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    if (!isAddMode) return;

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isAddMode, form, values, setIsError, setIsSubmittable]);

  //  Get all objects
  const getObjectsList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/objects`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: ObjectType[] = data.records;

          setObjectsList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (listRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setObjectsList]
  );

  //  Get all knowledge base items
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/master_invoices`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) {
            const errorData = await response.json();

            const errorText: { [key in typeof errorCode]: string } = {
              amountIsEmpty: "Сумма не может быть пустой",
              userFileIdNotFound: "Файл не найден в базе данных",
            };

            const errorFields: string[] = errorData.fields;
            const errorCode: "amountIsEmpty" | "userFileIdNotFound" =
              errorData.errorCode;

            if (errorFields.length) {
              const errorMessage = errorText[errorCode];

              errorFields.forEach((errorField) => {
                form.setFields([
                  {
                    name: errorField,
                    errors: [errorMessage],
                  },
                ]);
              });
            }

            throw new Error();
          }

          const data = await response.json();

          if (!data?.records) throw new Error();

          const invoices: MasterInvoice[] = data.records.map(
            (invoice: MasterInvoice) => ({
              ...invoice,
              type: "invoice",
            })
          );

          const accruals: MasterAccrual[] = data.accruals.map(
            (accrual: MasterAccrual) => ({
              ...accrual,
              type: "accrual",
            })
          );

          const records = [...invoices, ...accruals].sort((a, b) => {
            const dateA =
              a.type === "invoice"
                ? new Date(a.created_at).getTime()
                : new Date(a.date).getTime();
            const dateB =
              b.type === "invoice"
                ? new Date(b.created_at).getTime()
                : new Date(b.date).getTime();
            return dateA - dateB;
          });

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (list.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList, list, form]
  );

  //  When started, get all data
  useEffect(() => {
    getList();
    getObjectsList();
  }, [getList, getObjectsList]);

  const handleCancelButton = useCallback(
    (masterInvoice?: MasterInvoice) => {
      if (!masterInvoice?.id) return;

      let cancelComment = "";

      Modal.confirm({
        title: "Вы уверены, что хотите отменить этот счет?",
        content: (
          <div className={styles.modalContent}>
            <div className={styles.warningText}>
              Это действие нельзя будет отменить.
            </div>
            <Input.TextArea
              className={styles.commentInput}
              placeholder="Введите комментарий"
              onChange={(e) => (cancelComment = e.target.value)}
            />
          </div>
        ),
        okText: "Да, отменить",
        cancelText: "Отмена",
        onOk: async () => {
          if (!cancelComment.trim()) {
            Modal.error({
              title: "Ошибка",
              content: "Введите комментарий перед отменой.",
            });
            return Promise.reject();
          }

          setIsChangeStatusInProcess((prev) => ({
            ...prev,
            [masterInvoice.id]: true,
          }));

          const fetchData = async () => {
            const url = `${apiDomain}/api/records/master_invoices/${masterInvoice.id}`;
            const options: RequestInit = {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                status: "canceled",
                cancel_comment: cancelComment,
              }),
              credentials: "include",
            };

            try {
              const response = await fetch(url, options);
              if (!response.ok) throw new Error();

              await getList(true);
            } catch (error) {
              openNotificationWithError({
                message: "Ошибка при изменении статуса",
              });
            } finally {
              setIsChangeStatusInProcess((prev) => ({
                ...prev,
                [masterInvoice.id]: false,
              }));
            }
          };

          return fetchData();
        },
      });
    },
    [getList, openNotificationWithError, setIsChangeStatusInProcess]
  );

  const handleDetailsButton = useCallback((masterListItem?: MasterListItem) => {
    if (!masterListItem) return;

    if (masterListItem.type === "invoice") {
      Modal.info({
        title: "Детали счета",
        content: (
          <div className={styles.modalContent}>
            {!!masterListItem.object && (
              <div className={styles.modalRow}>
                Объект:{" "}
                {masterListItem.object.name
                  ? masterListItem.object.name
                  : masterListItem.object.address}
              </div>
            )}
            {!!(!!masterListItem.created_at && !!masterListItem.master) && (
              <div className={styles.modalRow}>
                Создан:{" "}
                {new Date(masterListItem.created_at).toLocaleString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                ,<br />
                мастер: #{masterListItem.master.id}{" "}
                {masterListItem.master?.name}
              </div>
            )}
            {!!(!!masterListItem.canceled_at && !!masterListItem.canceled) && (
              <div className={styles.modalRow}>
                Отменен:{" "}
                {new Date(masterListItem.canceled_at).toLocaleString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                ,<br />
                отменил: #{masterListItem.canceled.id}{" "}
                {masterListItem.canceled.name}
                {masterListItem.cancel_comment
                  ? `, комментарий: ${masterListItem.cancel_comment}`
                  : null}
              </div>
            )}
            {!!(
              !!masterListItem.confirmed_at && !!masterListItem.confirmed
            ) && (
              <div className={styles.modalRow}>
                Подтвержден:{" "}
                {new Date(masterListItem.confirmed_at).toLocaleString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                ,<br />
                подтвердил: #{masterListItem.confirmed.id}{" "}
                {masterListItem.confirmed.name}
              </div>
            )}
            {!!(!!masterListItem.paid_at && !!masterListItem.paid) && (
              <div className={styles.modalRow}>
                Оплата подтверждена:{" "}
                {new Date(masterListItem.paid_at).toLocaleString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                ,<br />
                подтвердил оплату: #{masterListItem.paid.id}{" "}
                {masterListItem.paid.name}
              </div>
            )}
          </div>
        ),
        okText: "Закрыть",
      });
    }
    if (masterListItem.type === "accrual") {
      Modal.info({
        title: "Детали начисления",
        content: (
          <div className={styles.modalContent}>
            {!!masterListItem.date && (
              <div className={styles.modalRow}>
                Дата начисления:{" "}
                {new Date(masterListItem.date).toLocaleString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </div>
            )}
            {!!masterListItem.object && (
              <div className={styles.modalRow}>
                Объект:{" "}
                {masterListItem.object.name
                  ? masterListItem.object.name
                  : masterListItem.object.address}
              </div>
            )}
          </div>
        ),
        okText: "Закрыть",
      });
    }
  }, []);

  //  Add button sets add mode or reset it
  const handleAddButton = useCallback(() => {
    navigate(`/finance/add`);
  }, [navigate]);

  const handleBackButton = useCallback(() => {
    navigate(`/finance`);
  }, [navigate]);

  const isInProcess = useMemo(() => {
    if (!!isAddMode && !!isPostInProcess) return true;

    return false;
  }, [isAddMode, isPostInProcess]);

  //  Amount changed
  useEffect(() => {
    const allValues = form.getFieldsValue();

    const amount = !allValues?.amount
      ? ""
      : (allValues?.amount as string)
          .replace(/[^\d.,]/g, "")
          .replace(/,/g, ".")
          .replace(/^(\d*(\.\d{0,2})?).*$/, "$1");

    form.setFieldsValue({
      amount: amount,
    });
  }, [form, values?.amount]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;

    const fetchData = async () => {
      setIsError(false);

      if (isAddMode) setIsPostInProcess(true);

      const url = `${apiDomain}/api/records/master_invoices`;

      const options: RequestInit = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          object_id: values?.object_id ? values.object_id : null,
          amount: values?.amount ? values.amount : null,
          invoice_link: values?.invoiceLink ? values.invoiceLink : "",
          comment: values?.comment ? values.comment : "",
          user_file_id: values.file?.[0].response?.[0]?.id
            ? values.file[0].response[0].id
            : values.file?.[0]?.uid && !isNaN(Number(values.file[0].uid))
            ? Number(values.file[0].uid)
            : null,
        }),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        getList(true);

        form.resetFields();
        form
          .validateFields({ validateOnly: true })
          .then(() => setIsSubmittable(true))
          .catch(() => setIsSubmittable(false));

        navigate(`/finance`);

        if (isAddMode) setIsPostInProcess(false);
      } catch (error) {
        setIsError(true);

        if (isAddMode) setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    isAddMode,
    form,
    values,
    isInProcess,
    navigate,
    setIsError,
    getList,
    setIsPostInProcess,
    setIsSubmittable,
  ]);

  const handleUploadFileChange = useCallback(
    (info: UploadChangeParam) => {
      setIsFileUploading(info.file.status === "uploading");
    },
    [setIsFileUploading]
  );

  const handlePreview = useCallback(async (previewFile: UploadFile) => {
    console.log(previewFile);
  }, []);

  //  Set columns for table
  const columns: ColumnsType = useMemo(() => {
    const statusMap: Record<string, string> = {
      unconfirmed: "Добавлен",
      confirmed: "Подтвержден",
      canceled: "Отменен",
      paid: "Оплачен",
    };

    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          return record.id;
        },
      },
      {
        title: "Дата",
        key: "created_at",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedDate = record.created_at
              ? new Date(record.created_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          const formattedDate = record.date
            ? new Date(record.date).toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : null;

          return formattedDate;
        },
      },
      {
        title: "Начислено",
        key: "accrued",
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return null;
          }

          const formattedAmount = parseFloat(record.calculated_sum)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

          return formattedAmount;
        },
      },
      {
        title: "Выплачено",
        key: "paid",
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (record.status !== "paid") return null;

            const formattedAmount = parseFloat(record.amount)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

            return formattedAmount;
          }

          return null;
        },
      },
      {
        title: "Тип операции",
        key: "operationType",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (record.status !== "paid") {
              const formattedAmount = parseFloat(record.amount)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

              return `Счет от мастера на ${formattedAmount} ₽`;
            }

            return "Счет от мастера";
          }

          return "Начисление оплаты";
        },
      },
      {
        title: "Статус",
        key: "status",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return statusMap[record.status] ?? null;
          }

          return null;
        },
      },
      {
        title: "Дата подтверждения",
        key: "confirmedAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["confirmed", "paid"].includes(record.status)) return null;

            const formattedDate = record.confirmed_at
              ? new Date(record.confirmed_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
      {
        title: "Дата оплаты",
        key: "paidAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["paid"].includes(record.status)) return null;

            const formattedDate = record.paid_at
              ? new Date(record.paid_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
      {
        key: "details",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;
          const recordStyles = record.styles;

          return (
            <Button
              onClick={() => handleDetailsButton(record)}
              className={recordStyles?.actionButton}
              disabled={isInProcess}
            >
              Подробнее
            </Button>
          );
        },
      },
      {
        key: "cancel",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;
          const recordStyles = record.styles;

          if (record.type === "invoice") {
            if (record.status !== "unconfirmed") return null;

            return (
              <Button
                onClick={() => handleCancelButton(record)}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
                loading={!!isChangeStatusInProcess?.[record?.id]}
              >
                Отменить
              </Button>
            );
          }

          return null;
        },
      },
    ];

    return result;
  }, [
    isInProcess,
    isChangeStatusInProcess,
    handleCancelButton,
    handleDetailsButton,
  ]);

  const tableData = useMemo(() => {
    return list.data;
  }, [list.data]);

  return (
    <MobileInnerWrapper>
      <Row justify="space-between" align="middle" className={styles.titleBlock}>
        <Col>
          <Title level={3} className={styles.title}>
            {isAddMode ? `Добавление счета` : `Финансы`}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={isAddMode ? handleBackButton : handleAddButton}
            disabled={isInProcess}
          >
            {isAddMode ? `Вернуться к списку` : `Добавить счет`}
          </Button>
        </Col>
      </Row>
      {!!isAddMode && (
        <>
          <Flex vertical gap={16}>
            <Form
              layout={"vertical"}
              form={form}
              onFinish={handleFinish}
              initialValues={{
                amount: "",
                invoiceLink: "",
                comment: "",
                userFileId: null,
              }}
            >
              <Form.Item
                name="object_id"
                label="Объект"
                validateTrigger={false}
                required={true}
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста, выберите объект!",
                  },
                ]}
              >
                <Select
                  placeholder="Выберите объект"
                  disabled={
                    isInProcess || !list.isLoaded || !objectsList.isLoaded
                  }
                >
                  {objectsList.data.map((object) => (
                    <Select.Option key={object.id} value={object.id}>
                      {object.name ? object.name : object.address}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="amount"
                label="Сумма"
                validateTrigger={false}
                required={true}
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста, введите сумму!",
                  },
                ]}
              >
                <Input
                  size="large"
                  name="amount"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item
                label="Прикрепить файл"
                name="file"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  action={`${apiDomain}/api/upload-file`}
                  withCredentials={true}
                  multiple={true}
                  onChange={handleUploadFileChange}
                  onPreview={handlePreview}
                  listType="picture"
                >
                  <Button
                    icon={<UploadOutlined />}
                    disabled={isFileUploading || !list.isLoaded}
                  >
                    {isFileUploading ? `Идет загрузка` : `Загрузить файл`}
                  </Button>
                </Upload>
              </Form.Item>
              <Form.Item
                name="invoiceLink"
                label="Ссылка на счет"
                validateTrigger={false}
                required={false}
              >
                <Input
                  size="large"
                  name="invoiceLink"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item
                name="comment"
                label="Комментарий"
                validateTrigger={false}
                required={false}
              >
                <TextArea
                  size="large"
                  name="comment"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  className={styles.button}
                  disabled={
                    !isSubmittable || !!isFileUploading || !list.isLoaded
                  }
                  htmlType="submit"
                  loading={isInProcess}
                >
                  Сохранить
                </Button>
              </Form.Item>
              {!!isError && (
                <Form.Item>
                  <Text type="danger">Не удалось сохранить счёт</Text>
                </Form.Item>
              )}
            </Form>
          </Flex>
        </>
      )}
      {!isAddMode && (
        <>
          <Table
            dataSource={tableData}
            columns={columns}
            rowKey="id"
            pagination={false}
            loading={!list.isLoaded}
            styles={styles}
          />
        </>
      )}
    </MobileInnerWrapper>
  );
};

export default Page;
